<template>
    <div class="chat-count-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('message.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('message.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('message.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="right">
                <label>{{ $t("report.viewAs") }}</label>
                <a-radio-group size="small" :value="uniqueUserCountMode">
                    <a-radio-button :value="true" @click="uniqueUserCountMode = true" :checked="uniqueUserCountMode">
                        {{ $t("report.uniqueUsers") }}
                    </a-radio-button>
                    <a-radio-button :value="false" @click="uniqueUserCountMode = false" :checked="!uniqueUserCountMode">
                        {{ $t("chatbotv2.allUsers") }}
                    </a-radio-button>
                </a-radio-group>
            </div>
        </div>
        <StackedAreaChart
            :title="$t('report.chatCountTimeseries')"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-if="!tableMode"
        />
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <div class="stats">
            <StatCard :title="$t('report.total')" :value="getStats().TotalSessionCount" :unit="$t('report.session')" />
            <StatCard
                :title="$t('report.average')"
                :value="getStats().AverageSessionCount"
                :unit="$t('report.session')"
            />
            <StatCard :title="$t('report.max')" :value="getStats().MaxSessionCount" :unit="$t('report.session')" />
            <StatCard :title="$t('report.min')" :value="getStats().MinSessionCount" :unit="$t('report.session')" />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import StackedAreaChart from "./StackedAreaChart.vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import StatCard from "./StatCard.vue";
import { dateToHourFromSecondsShortFormat } from "../../../../../../utils/filters";
import IconButton from "../../../../Settings/components/IconButton.vue";
import TableView from "./TableView.vue";
import { expertToExcel } from "../../../../../../utils/utils";

export default Vue.extend({
    name: "ChatCountChart",
    components: { TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        /**
         * [
         *     {
         *         "date": "2023-09-25T00:00:00+00:00",
         *         "items": [
         *             {
         *                 "createdDate": "2023-09-25T22:22:27.765Z",
         *                 "duration": 1202,
         *                 "successMatchCount": 0,
         *                 "failedMatchCount": 0
         *             },
         *         ]
         *     }
         * ]
         */
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            colors: ["#FFBF00"],
            legends: [this.$t("report.sessionCount")],
            stats: this.getStats(),
            uniqueUserCountMode: false,
            tableMode: false,
        };
    },
    methods: {
        humanizeDuration(totalSeconds) {
            return dateToHourFromSecondsShortFormat(totalSeconds, this.$i18n.locale, true);
            // return `${hours}h ${minutes}m ${seconds}s`;
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                    // scopedSlots: { customRender: "date" },
                },
                {
                    title: this.$t("report.sessionCount"),
                    dataIndex: "sessionCount",
                    key: "sessionCount",
                    // scopedSlots: { customRender: "sessionCount" },
                },
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.data;
            const rows = buckets.map((bucket) => {
                const sessionCount = this.uniqueUserCountMode ? bucket.uniqueVisitorCount : bucket.totalVisitorCount;

                const date = {
                    hourly: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    daily: moment(bucket.date).format("DD/MM/YYYY"),
                    monthly: moment(bucket.date).format("MM/YYYY"),
                }[timeSeriesType];
                return {
                    date,
                    sessionCount,
                };
            });
            return rows;
        },
        getStats() {
            const buckets = this.data?.buckets || [];
            const items = _.flatMap(buckets, (bucket) =>
                this.uniqueUserCountMode ? bucket.uniqueVisitorCount : bucket.totalVisitorCount
            );
            console.log("getStats", items);
            const TotalSessionCount = _.chain(items).sum().value();
            const AverageSessionCount = parseInt((_.mean(items) || 0).toFixed(1));
            const MaxSessionCount = parseInt((_.max(items) || 0).toFixed(1));
            const MinSessionCount = parseInt((_.min(items) || 0).toFixed(1));

            return {
                TotalSessionCount,
                AverageSessionCount,
                MaxSessionCount,
                MinSessionCount,
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.data;

            return buckets.map((item) => {
                if (timeSeriesType === "daily") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hourly") {
                    return moment(item.date).format("HH:mm");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.data;

            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => {
                    const value = this.uniqueUserCountMode ? bucket.uniqueVisitorCount : bucket.totalVisitorCount;
                    return value || 0;
                });
                const seriesData = {
                    name: legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: "rgb(55, 162, 255)",
                            },
                            {
                                offset: 1,
                                color: "rgb(255,255,255)",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.data;
            const data = buckets.map((bucket) => {
                const sessionCount = this.uniqueUserCountMode ? bucket.uniqueVisitorCount : bucket.totalVisitorCount;
                const date = {
                    hourly: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    daily: moment(bucket.date).format("DD/MM/YYYY"),
                    monthly: moment(bucket.date).format("MM/YYYY"),
                }[timeSeriesType];
                return {
                    date,
                    sessionCount,
                };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    date: "Toplam oturum",
                    sessionCount: stats.TotalSessionCount,
                },
                {
                    date: "Ortalama oturum",
                    sessionCount: stats.AverageSessionCount,
                },
                {
                    date: "Maksimum oturum",
                    sessionCount: stats.MaxSessionCount,
                },
                {
                    date: "Minimum oturum",
                    sessionCount: stats.MinSessionCount,
                },
            ];
            expertToExcel(excelData, `chatbot_session_count_timeseries.xlsx`, "Sheet 1");
        },
    },
    watch: {
        data: {
            handler: function (val, oldVal) {
                // this.stats = this.getStats();
            },
            deep: true,
        },
        uniqueUserCountMode(val) {
            this.legends = [val ? this.$t("message.sessionsUnique") : this.$t("message.sessionsAll")];
            // this.stats = this.getStats();
        },
    },
});
</script>

<style scoped lang="scss">
.chat-count-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
